import { useEffect, useState } from "react";
import type {
  AgentDTO,
  MemoryDTO,
  MemoryLevel,
  SubjectDTO,
  UserDTO,
} from "../../../shared/types";
import { confirmDialog } from "../../components/Dialog";
import {
  Button,
  ErrorText,
  inputCls,
  PageHeader,
  timeAgo,
} from "../../components/ui";
import { List, ListRow } from "../../components/List";
import { del, get, patch } from "../../lib/api";
import { changedBy } from "../../lib/memory";
import { TextLink } from "../../components/TextLink";

/** What each level of memory is, in the words an admin reads on the page. */
const LEVEL: Record<MemoryLevel, string> = {
  global: "Everyone",
  agent: "One agent",
  user: "One person",
  user_agent: "One person, one agent",
  scope: "One scope",
};

const LEVELS: MemoryLevel[] = ["global", "agent", "user", "user_agent", "scope"];

const selectCls = inputCls;

/** Every memory on the platform: what it says, whose it is, which agent wrote it and in which run. */
export function AdminMemories() {
  const [memories, setMemories] = useState<MemoryDTO[] | null>(null);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [people, setPeople] = useState<UserDTO[]>([]);
  const [scopes, setScopes] = useState<SubjectDTO[]>([]);
  const [filters, setFilters] = useState({
    q: "",
    level: "",
    agentId: "",
    userId: "",
    subjectId: "",
  });
  const [editing, setEditing] = useState<string | null>(null);
  // An agent's shared memory is its owners' to change (D5); the rest stays any admin's.
  const mayChange = (m: MemoryDTO) => m.level !== "agent" || agents.find((a) => a.id === m.agentId)?.youAnswerFor === true;
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = (f = filters) => {
    const params = new URLSearchParams(
      Object.entries(f).filter(([, v]) => v.trim()) as [string, string][],
    );
    params.set("limit", "500");
    return get<MemoryDTO[]>(`/api/memories?${params}`).then(setMemories, (e) =>
      setError(e.message),
    );
  };
  useEffect(() => {
    void load();
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
    get<UserDTO[]>("/api/users").then(
      (u) => setPeople(u.filter((x) => x.kind === "person")),
      () => {},
    );
    // Archived scopes keep their memories, so they stay in the filter.
    get<SubjectDTO[]>("/api/subjects?archived=all").then(setScopes, () => {});
  }, []);
  const set = (patchFilters: Partial<typeof filters>) => {
    const next = { ...filters, ...patchFilters };
    setFilters(next);
    void load(next);
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <PageHeader
        title="Memories"
        subtitle="Everything agents remember, across every person, agent and scope. Nothing here is loaded into a prompt: agents search it."
      />
      <form
        className="mb-5 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <input
          className={`${inputCls} min-w-52 flex-1`}
          placeholder="Search titles, content and tags"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
        />
        <span className="w-40">
          <select
            className={selectCls}
            value={filters.level}
            onChange={(e) => set({ level: e.target.value })}
            aria-label="Level"
          >
            <option value="">Any level</option>
            {LEVELS.map((k) => (
              <option key={k} value={k}>
                {LEVEL[k]}
              </option>
            ))}
          </select>
        </span>
        <span className="w-44">
          <select
            className={selectCls}
            value={filters.agentId}
            onChange={(e) => set({ agentId: e.target.value })}
            aria-label="Agent"
          >
            <option value="">Any agent</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </span>
        <span className="w-44">
          <select
            className={selectCls}
            value={filters.userId}
            onChange={(e) => set({ userId: e.target.value })}
            aria-label="Person"
          >
            <option value="">Any person</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </span>
        <span className="w-44">
          <select
            className={selectCls}
            value={filters.subjectId}
            onChange={(e) => set({ subjectId: e.target.value })}
            aria-label="Scope"
          >
            <option value="">Any scope</option>
            {scopes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName || s.key}
              </option>
            ))}
          </select>
        </span>
        <Button type="submit">Search</Button>
      </form>
      <ErrorText error={error} />
      {memories && (
        <List empty="No memories match that.">
          {memories.map((m) => (
            <ListRow
              key={m.id}
              title={<span className="min-w-0 truncate font-medium">{m.title}</span>}
              trailing={
                mayChange(m) && <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      editing === m.id
                        ? setEditing(null)
                        : (setEditing(m.id), setDraft(m.content))
                    }
                  >
                    {editing === m.id ? "Cancel" : "Edit"}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={async () =>
                      (await confirmDialog({ title: `Delete "${m.title}"?`, body: "The memory is gone for good.", confirm: "Delete memory", danger: true })) &&
                      del(`/api/memories/${m.id}`).then(
                        () => load(),
                        (e) => setError(e.message),
                      )
                    }
                  >
                    Delete
                  </Button>
                </>
              }
            >
              {editing === m.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    className={inputCls}
                    rows={5}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <Button
                    variant="primary"
                    disabled={!draft.trim()}
                    onClick={() =>
                      patch(`/api/memories/${m.id}`, { content: draft }).then(
                        () => (setEditing(null), load()),
                        (e) => setError(e.message),
                      )
                    }
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <p className="mt-1.5 line-clamp-6 text-sm whitespace-pre-wrap text-muted">
                  {m.content}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-faint">
                <span>{LEVEL[m.level]}</span>
                {m.scopeKey && <span>· {m.scopeName || m.scopeKey}</span>}
                {m.authorAgentSlug && (
                  <span>· written by {m.authorAgentSlug}</span>
                )}
                {changedBy(m) && <span>· changed by {changedBy(m)}</span>}
                {m.pinned && <span>· pinned</span>}
                <span>· updated {timeAgo(m.updatedAt)}</span>
                {m.createdByRun && (
                  <TextLink quiet to={`/admin/runs/${m.createdByRun}`}>
                    · the run it came from
                  </TextLink>
                )}
              </div>
            </ListRow>
          ))}
        </List>
      )}
    </div>
  );
}
